import { clientUserText, csvHeading } from '../../../../terminology/store';
import { displayText } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useContext, useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ThemeContext from '../../../ThemeContext';
import api, {
  getClientUsersByClientAdmin,
  getCases,
} from '../../../services/api';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import ReportPieChart from './ReportPieChart';

const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

const UserReport = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();

  const { resellerClients = [] } = location.state || {};

  const [data, setData] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const clientsDropdownRef = useRef(null);
  const reportsDropdownRef = useRef(null);
  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);

  const today = new Date();
  const currentMonthIndex = today.getMonth();
  const currentYear = today.getFullYear();

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: currentYear - 2015 + 3 }, (_, i) => 2015 + i);

  const [selectedMonth, setSelectedMonth] = useState(String(currentMonthIndex + 1));
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [appliedMonth, setAppliedMonth] = useState(String(currentMonthIndex + 1));
  const [appliedYear, setAppliedYear] = useState(String(currentYear));
  const [reportView, setReportView] = useState('report');

  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';

  const normalizedData = useMemo(() => {
    if (!resellerClients || resellerClients.length === 0) return [];
    return resellerClients
      .map(rc => ({
        reseller: rc.reseller || rc || null,
        clientAdmins: Array.isArray(rc.clientAdmins) ? rc.clientAdmins : (rc.client_admins || [])
      }))
      .filter(section =>
        section.reseller &&
        (section.reseller.id || section.reseller.username || section.reseller.reseller_name)
      );
  }, [resellerClients]);

  const getFirstAvailable = (obj = {}, keys = []) => {
    for (const k of keys) {
      if (!obj) continue;
      const v = obj[k];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return null;
  };

  const getPhone = (obj = {}) =>
    getFirstAvailable(obj, [
      'primary_phone',
      'primaryPhone',
      'phone',
      'phone_number',
      'mobile',
      'contact_phone',
      'contactNumber'
    ]) || null;

  const getZip = (obj = {}) =>
    getFirstAvailable(obj, [
      'zip',
      'zip_code',
      'zipCode',
      'postcode',
      'postal',
      'postal_code',
      'postalCode'
    ]) || null;

  const getPrimaryContactName = (obj = {}) =>
    getFirstAvailable(obj, [
      'primary_name',
      'primaryName',
      'primary_contact',
      'primaryContact',
      'contact',
      'contact_name',
      'contactName'
    ]) || null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const parseToYearMonth = (value) => {
    if (!value) return null;
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return null;
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    } catch {
      return null;
    }
  };

  const isDateOnOrBeforeApplied = (dateVal) => {
    const parsed = parseToYearMonth(dateVal);
    if (!parsed) return false;
    const aYear = parseInt(appliedYear, 10);
    const aMonth = parseInt(appliedMonth, 10);
    if (parsed.year < aYear) return true;
    if (parsed.year > aYear) return false;
    return parsed.month <= aMonth;
  };

  const parseDateFields = (obj = {}) => {
    return getFirstAvailable(obj, ['created_at', 'createdAt', 'start_date', 'date']) || null;
  };

  const filterCasesForClientAdmin = (allCases = [], ca = {}) => {
    if (!Array.isArray(allCases)) return [];
    const caId = ca.id ?? ca.client_id ?? ca.clientId ?? null;
    const caName =
      getFirstAvailable(ca, [
        'client_name',
        'firm_name',
        'name',
        'username',
        'first_name',
        'last_name'
      ]) || null;

    return allCases.filter(c => {
      if (!c) return false;
      const cClientId = c.client_id ?? c.clientId ?? null;
      const cManagerId = c.case_manager_id ?? c.assign_case_manager ?? c.case_manager ?? c.manager_id ?? null;

      if (caId != null) {
        if (String(cClientId) === String(caId)) return true;
        if (String(cManagerId) === String(caId)) return true;
        if (String(c.id) === String(caId)) return true;
      }

      const cFirm = getFirstAvailable(c, ['firm_name', 'client_name', 'firm', 'client']) || '';
      if (caName && cFirm && String(cFirm).toLowerCase().includes(String(caName).toLowerCase())) return true;

      const csFirst = getFirstAvailable(c, ['first_name', 'manager_first_name']) || '';
      const csLast = getFirstAvailable(c, ['last_name', 'manager_last_name']) || '';
      if (caName && (csFirst || csLast)) {
        const combined = `${csFirst} ${csLast}`.trim().toLowerCase();
        if (combined && String(caName).toLowerCase().includes(combined)) return true;
      }

      return false;
    });
  };
const chunkArray = (array = [], size = 5) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};
  const loadClientUsers = async (clientAdmins) => {
  const chunks = chunkArray(clientAdmins, 5); // 👈 only 5 at a time
  let results = [];

  for (let chunk of chunks) {
    const res = await Promise.all(
      chunk.map(async (ca) => {
        try {
          const r = await getClientUsersByClientAdmin(ca.id);
          return { ...ca, users: r.data?.data || [] };
        } catch {
          return { ...ca, users: [] };
        }
      })
    );
    results.push(...res);
    await delay(200); // 👈 small pause
  }

  return results;
};

  const fetchAllCasesOnce = async () => {
    try {
      const res = await getCases();
      return Array.isArray(res.data) ? res.data : (res.data?.data || []);
    } catch {
      return [];
    }
  };

  const delay = (ms) => new Promise(res => setTimeout(res, ms));

const buildSessionsByCaseId = async (allCases = []) => {
  const sessionsByCase = {};
  const chunks = chunkArray(allCases, 5); // 👈 limit cases

  for (let group of chunks) {
    await Promise.all(
      group.map(async (cs) => {
        try {
          const res = await api.get(`/cases/${cs.id}/deposition-sessions`);
          sessionsByCase[cs.id] = res.data?.[0]?.introducing_party || null;
        } catch {
          sessionsByCase[cs.id] = null;
        }
      })
    );

    await delay(300); // 👈 throttle
  }

  return sessionsByCase;
};

  const buildCaseOwnershipMap = async (allCases = []) => {
    if (!Array.isArray(allCases) || allCases.length === 0) return {};
    try {
      const [clientAdminsResp, usersResp] = await Promise.all([
        api.get('/client-admins'),
        api.get('/users'),
      ]);
      const clientAdmins = clientAdminsResp.data?.data || clientAdminsResp.data || [];
      const users = usersResp.data?.users || [];
      const usersById = {};
      users.forEach(u => u?.id && (usersById[String(u.id)] = u));

      const clientAdminByEmail = {};
      const clientAdminById = {};

      clientAdmins.forEach(ca => {
        if (!ca) return;
        if (ca.id != null) clientAdminById[String(ca.id)] = ca;
        const emails = [
          (ca.primary_email || '').toLowerCase(),
          (ca.email || '').toLowerCase(),
          (ca.user?.email || '').toLowerCase(),
        ].filter(Boolean);
        emails.forEach(em => {
          clientAdminByEmail[em] = ca;
        });
      });

      const ownership = {};
      allCases.forEach(cs => {
        if (!cs || cs.id == null) return;
        let clientUser = cs.client || cs.client_user || null;
        if (!clientUser) {
          const uid = cs.client_id ?? cs.clientId ?? null;
          if (uid != null) clientUser = usersById[String(uid)];
        }

        const emailCandidates = [];
        if (clientUser?.email) emailCandidates.push(clientUser.email);
        if (cs.email) emailCandidates.push(cs.email);
        if (cs.client_email) emailCandidates.push(cs.client_email);
        if (cs.contact_email) emailCandidates.push(cs.contact_email);
        if (cs.introducing_party) {
          const m = String(cs.introducing_party).match(EMAIL_REGEX);
          if (m && m[0]) emailCandidates.push(m[0]);
        }

        let matchedCA = null;
        for (const raw of emailCandidates) {
          const em = (raw || '').toLowerCase();
          if (!em) continue;
          if (clientAdminByEmail[em]) {
            matchedCA = clientAdminByEmail[em];
            break;
          }
        }

        if (!matchedCA) {
          const caIdFromCase = cs.client_admin_id ?? cs.clientAdminId ?? cs.client_id ?? null;
          if (caIdFromCase != null) matchedCA = clientAdminById[String(caIdFromCase)] || null;
        }

        if (matchedCA) {
          const resellerId = matchedCA.reseller_id ?? matchedCA.resellerId ?? matchedCA.reseller?.id ?? null;
          ownership[cs.id] = { clientAdminId: matchedCA.id, resellerId };
        }
      });

      return ownership;
    } catch {
      return {};
    }
  };

  useEffect(() => {
    const enrichData = async () => {
      if (normalizedData.length === 0) {
        setLoadingUsers(false);
        setData([]);
        return;
      }

      setLoadingUsers(true);
      const allCases = await fetchAllCasesOnce();
      const sessionsByCaseId = await buildSessionsByCaseId(allCases);
      const ownershipMap = await buildCaseOwnershipMap(allCases);
      const enriched = [...normalizedData];

      for (let section of enriched) {
        section.clientAdmins = await loadClientUsers(section.clientAdmins);
        section.clientAdmins = section.clientAdmins.map(ca => {
          let matchedCases = allCases.filter(cs => {
            const own = ownershipMap[cs.id];
            if (!own || own.clientAdminId == null) return false;
            return String(own.clientAdminId) === String(ca.id);
          });

          if (!matchedCases.length) {
            const unownedCases = allCases.filter(cs => !ownershipMap[cs.id]);
            matchedCases = filterCasesForClientAdmin(unownedCases, ca);
          }

          ca.cases = matchedCases.map(cs => ({
            ...cs,
            _introducing_from_sessions:
              sessionsByCaseId[cs.id] || cs.introducing_party || cs.introducingParty || null,
          }));
          return ca;
        });
      }

      setData(enriched);
      setLoadingUsers(false);
    };

    enrichData();
  }, [normalizedData, appliedMonth, appliedYear]);

  const handleUpdate = () => {
    setAppliedMonth(String(selectedMonth));
    setAppliedYear(String(selectedYear));
    const monthName = months[parseInt(selectedMonth, 10) - 1];
    toast.success(`Report updated for ${monthName} ${selectedYear}`);
  };

  const handlePrint = () => window.print();

  const exportToCSV = () => {
    const rows = [];
    const header = [
      'Reseller Id', 'Reseller Name', 'Reseller City', 'Reseller State',
      'Client Admin Id', 'Client Admin/Firm', 'Client City', 'Client State',
      'User Id', 'User Name', 'Username', 'User City', 'User State', 'User Created', 'User Status',
      'Case Id', 'Case Name', 'Case Matter #', 'Case Firm', 'Case Date'
    ];
    rows.push(header.map(value => csvHeading(clientUserText(value))));

    const sourceSections = data && data.length ? data : visibleData;
    sourceSections.forEach(section => {
      const reseller = section.reseller || {};
      const rIdRaw = reseller.id ?? reseller.reseller_id ?? reseller._id ?? '';
      const isUnknownReseller = !rIdRaw || String(rIdRaw) === 'unknown';
      if (isUnknownReseller) return;

      const rId = rIdRaw;
      const rName = getFirstAvailable(reseller, ['reseller_name', 'name', 'username']) || '';
      const rCity = getFirstAvailable(reseller, ['city', 'town', 'municipality']) || '';
      const rState = getFirstAvailable(reseller, ['state', 'region', 'province']) || '';

      (section.clientAdmins || []).forEach(ca => {
        const caResellerId = ca.reseller_id ?? ca.resellerId ?? ca.reseller?.id ?? null;
        if (!caResellerId) return;

        const caId = ca.id ?? '';
        const caName = getFirstAvailable(ca, ['client_name', 'firm_name', 'name']) || '';
        const caCity = getFirstAvailable(ca, ['city', 'town']) || '';
        const caState = getFirstAvailable(ca, ['state', 'region']) || '';

        const users =
          Array.isArray(ca.users) && ca.users.length > 0
            ? ca.users
            : [{ id: '', name: '', username: '', city: '', state: '', created_at: '', deleted_at: '' }];

        const casesList =
          Array.isArray(ca.cases) && ca.cases.length > 0
            ? ca.cases
            : [{ id: '', case_name: '', case_matter_number: '', firm_name: '', created_at: '' }];

        users.forEach(u => {
          (casesList.length > 0 ? casesList : [null]).forEach(cs => {
            const row = [
              rId,
              rName,
              rCity,
              rState,
              caId,
              caName,
              caCity,
              caState,
              u.id ?? '',
              (u.first_name || u.last_name)
                ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                : (u.name || ''),
              u.username || '',
              u.city || '',
              u.state || '',
              formatDate(u.created_at),
              u.deleted_at ? 'Deleted' : 'Active',
              cs?.id ?? '',
              cs?.case_name ?? '',
              cs?.case_matter_number ?? '',
              cs?.firm_name ?? '',
              formatDate(cs?.created_at)
            ];
            rows.push(row);
          });
        });
      });
    });

    const csv = rows
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const monthLabel = months[parseInt(appliedMonth, 10) - 1] || 'month';
    const fname = `user-report-${monthLabel}-${appliedYear}.csv`;

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', fname);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Exported CSV created');
  };

  const handleExport = () => exportToCSV();

  const visibleData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const out = [];

    for (const section of data) {
      const reseller = section.reseller || {};
      const resellerDate = getFirstAvailable(reseller, ['reseller_since', 'start_date', 'created_at', 'createdAt']);
      if (!isDateOnOrBeforeApplied(resellerDate)) continue;

      const filteredAdmins = (section.clientAdmins || [])
        .map(ca => {
          const caDate = getFirstAvailable(ca, ['start_date', 'created_at', 'createdAt']);
          if (!isDateOnOrBeforeApplied(caDate)) return null;

          const caUsers = (ca.users || []).filter(u => {
            const uDate = getFirstAvailable(u, ['created_at', 'createdAt']);
            return isDateOnOrBeforeApplied(uDate);
          });

          const caCases = (ca.cases || []).filter(cs => {
            const csDate = parseDateFields(cs);
            return isDateOnOrBeforeApplied(csDate);
          });

          return { ...ca, users: caUsers, cases: caCases };
        })
        .filter(Boolean);

      out.push({ reseller, clientAdmins: filteredAdmins });
    }

    return out;
  }, [data, appliedMonth, appliedYear]);

  const sortClientAdminsByAppliedMonth = (clientAdmins = []) => {
    return [...clientAdmins].sort((a, b) => {
      const aDate = getFirstAvailable(a, ['start_date', 'created_at', 'createdAt']);
      const bDate = getFirstAvailable(b, ['start_date', 'created_at', 'createdAt']);
      const aTime = aDate ? new Date(aDate).getTime() : 0;
      const bTime = bDate ? new Date(bDate).getTime() : 0;
      return bTime - aTime;
    });
  };

  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
  const role = userInfo.role || 'reseller';

  const navItems = {
    superadmin: ['resellers', 'clients', 'reports', 'notify', 'admins', 'account'],
    admin: ['resellers', 'clients', 'reports', 'account'],
    reseller: ['clients', 'reports', 'account']
  };

  const tabs = navItems[role] || navItems.reseller;
  const isActive = (tab) => location.pathname.includes(`/${tab}`);
  const basePath =
    role === 'superadmin'
      ? '/superadmin'
      : role === 'admin'
      ? '/admin'
      : `/${userInfo.username}`;

  const handleTabClick = (tab) => {
    if (tab === 'clients') {
      navigate(`${basePath}/clients`);
    } else if (tab === 'reports') {
      navigate(`${basePath}/reports?sub=user`);
    } else {
      navigate(`${basePath}/${tab}`);
    }
  };

  const handleClientSubTab = (subTab) => {
    const path = subTab === 'enterprise' ? 'enterprise-clients' : 'clients';
    navigate(`${basePath}/${path}`);
    setIsClientsDropdownOpen(false);
  };

  const handleReportSubTab = (subTabKey) => {
    navigate(`${basePath}/reports?sub=${subTabKey}`);
    setIsReportsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientsDropdownRef.current && !clientsDropdownRef.current.contains(e.target)) {
        setIsClientsDropdownOpen(false);
      }
      if (reportsDropdownRef.current && !reportsDropdownRef.current.contains(e.target)) {
        setIsReportsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const actionButtonStyle = {
    background: theme.gradients?.secondary || theme.colors.secondary,
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    fontWeight: 'bold',
    fontSize: '12px',
    cursor: 'pointer',
    minWidth: '100px',
    height: '32px',
    fontFamily,
  };

  const dropdownStyle = {
    padding: '8px 36px 8px 14px',
    borderRadius: '999px',
    border: '1px solid #d0d7de',
    fontSize: '12px',
    height: '36px',
    minWidth: '120px',
    cursor: 'pointer',
    outline: 'none',
    fontFamily,
    backgroundColor: '#fff',
    color: theme.colors.textPrimary
  };

  const appliedMonthYearLabel = `${months[parseInt(appliedMonth, 10) - 1]} ${appliedYear}`;

  return (
    <div
      style={{
        fontFamily,
        backgroundColor: theme.colors.bgPrimary,
        minHeight: '100vh',
        paddingBottom: '3rem'
      }}
    >
      {/* TOP NAVIGATION TABS */}
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}
      >
        {tabs.map((tab) => {
          const isActiveTab = isActive(tab);
          const label =
            tab === 'account'
              ? 'My Account'
              : tab === 'notify'
              ? 'Notify User'
              : tab.charAt(0).toUpperCase() + tab.slice(1);

          const iconClass =
            tab === 'resellers'
              ? 'briefcase-fill'
              : tab === 'clients'
              ? 'people-fill'
              : tab === 'reports'
              ? 'file-earmark-bar-graph-fill'
              : tab === 'notify'
              ? 'bell-fill'
              : tab === 'admins'
              ? 'buildings-fill'
              : 'person-lines-fill';

          const isClientsTab = tab === 'clients' && role !== 'reseller';
          const isReportsTab = tab === 'reports';

          return (
            <div
              key={tab}
              style={{ position: 'relative', flex: '1 1 0', minWidth: '100px' }}
              ref={isClientsTab ? clientsDropdownRef : isReportsTab ? reportsDropdownRef : null}
              onMouseEnter={() => {
                if (isClientsTab) setIsClientsDropdownOpen(true);
                if (isReportsTab) setIsReportsDropdownOpen(true);
              }}
              onMouseLeave={() => {
                if (isClientsTab) setIsClientsDropdownOpen(false);
                if (isReportsTab) setIsReportsDropdownOpen(false);
              }}
            >
              <button
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  border: 'none',
                  background: isActiveTab
                    ? theme.gradients?.primary || theme.colors.primary
                    : theme.gradients?.secondary || theme.colors.secondary,
                  color: '#fff',
                  cursor: isClientsTab || isReportsTab ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontFamily,
                }}
                onClick={() => !(isClientsTab || isReportsTab) && handleTabClick(tab)}
              >
                <i className={`bi bi-${iconClass}`} style={{ fontSize: '1rem' }}></i>
                {label}
              </button>

              {isClientsTab && isClientsDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '6px',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    zIndex: 30,
                    width: '100%',
                    minWidth: '160px',
                    overflow: 'hidden',
                    fontFamily
                  }}
                >
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      textAlign: 'left',
                      background: 'transparent',
                      color: theme.colors.textPrimary,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily
                    }}
                    onClick={() => handleClientSubTab('core')}
                    onMouseOver={e => (e.target.style.background = theme.gradients?.secondary || theme.colors.secondary)}
                    onMouseOut={e => (e.target.style.background = 'transparent')}
                  >{displayText(" Court Client ")}</button>
                  <hr style={{ margin: 0, borderTop: `1px solid ${theme.colors.borderColor}` }} />
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      textAlign: 'left',
                      background: 'transparent',
                      color: theme.colors.textPrimary,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily
                    }}
                    onClick={() => handleClientSubTab('enterprise')}
                    onMouseOver={e => (e.target.style.background = theme.gradients?.secondary || theme.colors.secondary)}
                    onMouseOut={e => (e.target.style.background = 'transparent')}
                  >{displayText(" Enterprise Client ")}</button>
                </div>
              )}

              {isReportsTab && isReportsDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '6px',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    zIndex: 30,
                    width: '100%',
                    minWidth: '180px',
                    overflow: 'hidden',
                    fontFamily
                  }}
                >
                  {[
                    { key: 'user', label: 'User Report' },
                    { key: 'commission', label: 'Commission Report' },
                    { key: 'usage', label: 'Usage Report' }
                  ].map((item, idx, arr) => (
                    <React.Fragment key={item.key}>
                      <button
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          background:
                            item.key === 'user'
                              ? theme.gradients?.primary || theme.colors.primary
                              : 'transparent',
                          color: item.key === 'user' ? '#fff' : theme.colors.textPrimary,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontFamily
                        }}
                        onClick={() => handleReportSubTab(item.key)}
                        onMouseOver={e => item.key !== 'user' && (e.target.style.background = theme.gradients?.secondary || theme.colors.secondary)}
                        onMouseOut={e => item.key !== 'user' && (e.target.style.background = 'transparent')}
                      >
                        {item.label}
                      </button>
                      {idx < arr.length - 1 && (
                        <hr style={{ margin: 0, borderTop: `1px solid ${theme.colors.borderColor}` }} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* BREADCRUMB */}
      <div style={{ padding: '0 0.75rem', marginBottom: '0.5rem' }}>
        <span
          style={{
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: 500,
            color: theme.colors.primary
          }}
          onClick={() => navigate(`${basePath}/reports?sub=user`)}
        >{clientUserText(" User Report ")}</span>
        <span style={{ margin: '0 4px' }}>/</span>
        <span style={{ color: theme.colors.textPrimary }}>
          {reportView === 'pie-chart' ? 'Pie Chart' : 'Report'}
        </span>
      </div>

      {/* TOOLBAR */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '1rem',
          padding: '0 0.75rem',
          marginBottom: '1.25rem'
        }}
      >
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={handlePrint} style={actionButtonStyle}>
            Print
          </button>
          <button type="button" onClick={handleExport} style={actionButtonStyle}>
            Export
          </button>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontWeight: '500', fontSize: '12px' }}>Month:</span>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={dropdownStyle}>
              {months.map((m, i) => (
                <option key={i + 1} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontWeight: '500', fontSize: '12px' }}>Year:</span>
            <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} style={dropdownStyle}>
              {years.map(y => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <button
              onClick={handleUpdate}
              style={{
                ...actionButtonStyle,
                background: theme.gradients?.primary || theme.colors.primary
              }}
            >
              Update
            </button>

            <select
              value={reportView}
              onChange={(e) => setReportView(e.target.value)}
              style={dropdownStyle}
            >
              <option value="report">Report</option>
              <option value="pie-chart">Pie Chart</option>
            </select>
          </div>
        </div>
      </div>

      {reportView === 'pie-chart' && (
        <div style={{ margin: '0 0.75rem 20px' }}>
          <ReportPieChart
            reportType="user"
            rawData={visibleData}
            isLoading={loadingUsers}
            defaultViewMode="pie-chart"
          />
        </div>
      )}

      {reportView === 'report' && (
        <div
          style={{
            backgroundColor: '#ffffff',
            padding: '1rem',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            margin: '0 0.75rem 20px',
            fontFamily
          }}
        >
          {loadingUsers ? (
            <div style={{ textAlign: 'center', padding: '4rem 0', color: '#666' }}>
              Loading report data...
            </div>
          ) : visibleData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#999' }}>
              <h4>No report data available for selected date</h4>
              <p>{displayText("No clients found under selected resellers for this applied month/year.")}</p>
            </div>
          ) : (
            visibleData.map(({ reseller, clientAdmins }) => {
              const resellerDisplayName = getFirstAvailable(reseller, ['reseller_name', 'name', 'username']) || 'N/A';
              const city = getFirstAvailable(reseller, ['city', 'town', 'municipality']) || '';
              const state = getFirstAvailable(reseller, ['state', 'region', 'province']) || '';
              const zip = getZip(reseller) || '';
              const since = getFirstAvailable(reseller, ['reseller_since', 'start_date', 'created_at', 'createdAt']) || null;
              const status = getFirstAvailable(reseller, ['client_setting', 'status']) || (reseller.is_active === false ? 'Deactivated' : 'Active');
              const resellerPrimaryContact = getPrimaryContactName(reseller) || 'N/A';
              const resellerEmail = getFirstAvailable(reseller, ['primary_email', 'primaryEmail', 'email', 'contact_email']) || null;
              const resellerPhone = getPhone(reseller) || null;

              const sortedClientAdmins = sortClientAdminsByAppliedMonth(clientAdmins);

              return (
                <div key={reseller.id || reseller.username || resellerDisplayName} style={{ marginBottom: '3rem' }}>
                  {/* RESELLER HEADER */}
                  <div
                    style={{
                      background: theme.gradients?.primary || theme.colors.primary,
                      color: 'white',
                      padding: '1rem 1.5rem',
                      borderRadius: '12px 12px 0 0',
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 1fr 0.9fr',
                      gap: '1rem',
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        right: '1rem',
                        top: '0.8rem',
                        fontSize: '0.8rem',
                        background: 'rgba(255,255,255,0.12)',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255,255,255,0.18)'
                      }}
                    >
                      {appliedMonthYearLabel}
                    </div>
                    <div>
                      <div style={{ fontSize: '1.02em', fontWeight: 700 }}>{displayText(" Reseller: ")}{resellerDisplayName}
                      </div>
                      <div style={{ marginTop: '6px', fontWeight: 500 }}>
                        {city || 'N/A'}
                        {(city && state) ? ', ' : ''}
                        {state || ''}
                        {zip ? `, ${zip}` : ''}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600 }}>Since: {formatDate(since)}</div>
                      <div style={{ marginTop: '8px', fontWeight: 600 }}>
                        Status <span style={{ fontWeight: 700 }}>{status}</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', paddingRight: '7rem' }}>
                      <div style={{ fontWeight: 600 }}>Contact: {resellerPrimaryContact}</div>
                      <div style={{ marginTop: '6px' }}>Email {resellerEmail || 'N/A'}</div>
                      <div style={{ marginTop: '6px' }}>Phone {resellerPhone || 'N/A'}</div>
                    </div>
                  </div>

                  {/* CLIENT ADMIN SUMMARY TABLE */}
                  {sortedClientAdmins.length > 0 ? (
                    <div style={{ border: '1px solid #dee2e6', borderTop: 'none' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ padding: '0.8rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Firm</th>
                            <th style={{ padding: '0.8rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>{clientUserText("Per User/Month")}</th>
                            <th style={{ padding: '0.8rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>{clientUserText("User/Month/Case")}</th>
                            <th style={{ padding: '0.8rem', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedClientAdmins.map((ca) => (
                            <tr key={ca.id}>
                              <td style={{ padding: '0.8rem' }}>
                                {getFirstAvailable(ca, ['client_name', 'firm_name', 'name']) || 'Unnamed Firm'}
                              </td>
                              <td style={{ padding: '0.8rem', textAlign: 'center' }}>0</td>
                              <td style={{ padding: '0.8rem', textAlign: 'center' }}>0</td>
                              <td
                                style={{
                                  padding: '0.8rem',
                                  textAlign: 'right',
                                  fontWeight: 'bold',
                                  color: theme.colors.primary
                                }}
                              >
                                $0.00
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td
                              colSpan={3}
                              style={{
                                padding: '0.8rem',
                                textAlign: 'right',
                                borderTop: '2px solid #dee2e6',
                                fontWeight: 700
                              }}
                            >
                              Total Subtotal
                            </td>
                            <td
                              style={{
                                padding: '0.8rem',
                                textAlign: 'right',
                                borderTop: '2px solid #dee2e6',
                                fontWeight: 700,
                                color: theme.colors.primary
                              }}
                            >
                              $0.00
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: '1.5rem',
                        textAlign: 'center',
                        color: '#666',
                        border: '1px solid #dee2e6',
                        borderTop: 'none'
                      }}
                    >
                      No unassociated firms for this date range.
                    </div>
                  )}

                  {/* CLIENT ADMIN DETAILS + CASES */}
                  {sortedClientAdmins.map((ca) => {
                    const firmDisplayName = getFirstAvailable(ca, ['client_name', 'firm_name', 'name']) || 'Unnamed Firm';
                    const caCity = getFirstAvailable(ca, ['city', 'town']) || 'N/A';
                    const caState = getFirstAvailable(ca, ['state', 'region']) || 'N/A';
                    const caZip = getZip(ca) || '';
                    const caSince = getFirstAvailable(ca, ['start_date', 'created_at', 'createdAt']) || null;
                    const caStatus = getFirstAvailable(ca, ['client_setting', 'status']) || (ca.is_active === false ? 'Deactivated' : 'Active');
                    const caPrimaryContact = getPrimaryContactName(ca) || 'N/A';
                    const caEmail = getFirstAvailable(ca, ['primary_email', 'primaryEmail', 'email', 'contact_email']) || null;
                    const caPhone = getPhone(ca) || null;
                    const users = Array.isArray(ca.users) ? ca.users : [];
                    const casesList = Array.isArray(ca.cases) ? ca.cases : [];

                    return (
                      <div key={ca.id} style={{ marginTop: '2rem' }}>
                        {/* CLIENT ADMIN HEADER */}
                        <div
                          style={{
                            background: theme.gradients?.secondary || theme.colors.secondary,
                            color: 'white',
                            padding: '1rem 1.5rem',
                            borderRadius: '12px 12px 0 0',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '1rem',
                            alignItems: 'start',
                            fontSize: 'clamp(11px, 2.5vw, 13px)',
                            position: 'relative',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              right: '1rem',
                              top: '0.8rem',
                              fontSize: '0.75rem',
                              color: 'white',
                              background: 'rgba(255,255,255,0.12)',
                              padding: '3px 7px',
                              borderRadius: '6px',
                              border: '1px solid rgba(255,255,255,0.14)',
                            }}
                          >
                            {appliedMonthYearLabel}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '1.02em' }}>
                              Firm {firmDisplayName}
                            </div>
                            <div style={{ marginTop: '8px', fontWeight: 500, lineHeight: 1.3 }}>
                              {caCity}
                              {(caCity && caState) ? ', ' : ''}
                              {caState}
                              {caZip ? `, ${caZip}` : ''}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>{displayText("Client Since ")}{formatDate(caSince)}</div>
                            <div style={{ marginTop: '8px', fontWeight: 600 }}>
                              Status <span style={{ fontWeight: 700 }}>{caStatus}</span>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', paddingRight: '6.5rem' }}>
                            <div style={{ fontWeight: 600 }}>Contact {caPrimaryContact}</div>
                            <div style={{ marginTop: '6px' }}>Email {caEmail || 'N/A'}</div>
                            <div style={{ marginTop: '6px' }}>Phone {caPhone || 'N/A'}</div>
                          </div>
                        </div>

                        {/* USERS TABLE */}
                        <div
                          style={{
                            background: 'white',
                            border: '1px solid #dee2e6',
                            borderTop: 'none',
                            borderRadius: '0 0 12px 12px'
                          }}
                        >
                          {users.length > 0 ? (
                            <>
                              <table
                                style={{
                                  width: '100%',
                                  borderCollapse: 'collapse',
                                  fontSize: 'clamp(10px, 2.5vw, 12px)'
                                }}
                              >
                                <thead>
                                  <tr style={{ backgroundColor: '#f1f3f5' }}>
                                    <th style={{ padding: '0.7rem', textAlign: 'left' }}>{clientUserText("User")}</th>
                                    <th style={{ padding: '0.7rem', textAlign: 'left' }}>Username</th>
                                    <th style={{ padding: '0.7rem', textAlign: 'left' }}>City</th>
                                    <th style={{ padding: '0.7rem', textAlign: 'left' }}>State/Region</th>
                                    <th style={{ padding: '0.7rem', textAlign: 'left' }}>Created</th>
                                    <th style={{ padding: '0.7rem', textAlign: 'left' }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {users.map((user) => (
                                    <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '0.7rem' }}>
                                        {(user.first_name || user.last_name)
                                          ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                          : (user.name || 'N/A')}
                                      </td>
                                      <td style={{ padding: '0.7rem' }}>{user.email || 'N/A'}</td>
                                      <td style={{ padding: '0.7rem' }}>{user.city || 'N/A'}</td>
                                      <td style={{ padding: '0.7rem' }}>{user.state || 'N/A'}</td>
                                      <td style={{ padding: '0.7rem' }}>{formatDate(user.created_at)}</td>
                                      <td style={{ padding: '0.7rem' }}>{user.deleted_at ? 'Deleted' : 'Active'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <div
                                style={{
                                  padding: '0.4rem 1rem',
                                  textAlign: 'right',
                                  fontSize: '0.85rem',
                                  backgroundColor: '#f5f5f5',
                                  borderTop: '1px solid #dee2e6'
                                }}
                              >{clientUserText(" Total Users ")}{users.length}
                              </div>
                            </>
                          ) : (
                            <div
                              style={{
                                padding: '1.25rem',
                                textAlign: 'center',
                                color: '#666',
                                fontWeight: 600
                              }}
                            >{clientUserText(" No unassociated users for this date range. ")}</div>
                          )}
                        </div>

                        {/* CASES */}
                        {casesList.length > 0 ? (
                          casesList.map((cs) => {
                            const caseName = getFirstAvailable(cs, ['case_name', 'name', 'title']) || 'Unnamed Case';
                            const caseMatter = getFirstAvailable(cs, ['case_matter_number', 'matter_number', 'case_number', 'matter_no']) || 'N/A';
                            const caseFirm = getFirstAvailable(cs, ['firm_name', 'client_name', 'firm']) || firmDisplayName || 'N/A';
                            const caseDate = parseDateFields(cs);
                            const caseContact = getFirstAvailable(cs, ['contact', 'contact_name', 'primary_contact', 'client_contact', 'attorney', 'first_name']) || 'N/A';
                            const casePhone = getPhone(cs) || getFirstAvailable(cs, ['phone', 'phone_number', 'contact_phone']) || 'N/A';
                            const introducingStr = cs._introducing_from_sessions || getFirstAvailable(cs, ['introducing_party', 'introducingParty']) || '';

                            let introEmail = getFirstAvailable(cs, ['contact_email', 'email', 'primary_email']) || null;
                            if (!introEmail && introducingStr) {
                              const m = introducingStr.match(EMAIL_REGEX);
                              if (m && m[0]) introEmail = m[0];
                            }

                            let introRole = '';
                            if (introducingStr) {
                              const rm = introducingStr.match(/\(([^)]+)\)/);
                              if (rm && rm[1]) introRole = rm[1];
                            }

                            const userCell = `Exhibit${introRole ? `, ${introRole}` : ''}`;
                            const usernameCell = introEmail || introducingStr || 'N/A';
                            const caseEmail = introEmail || getFirstAvailable(cs, ['contact_email', 'email', 'primary_email']) || 'N/A';
                            const caseAddress = getFirstAvailable(cs, ['address', 'location', 'address_line', 'street_address']) || 'N/A';
                            const rowCity = cs.city || cs.location || caCity || 'N/A';
                            const rowState = cs.state || caState || 'N/A';
                            const rowCreated = formatDate(caseDate);
                            const rowDeleted = cs.deleted_at ? formatDate(cs.deleted_at) : '–';

                            return (
                              <div
                                key={cs.id || `${caseName}-${caseMatter}`}
                                style={{
                                  marginTop: '1.5rem',
                                  border: '1px solid #d1d1d1',
                                  borderRadius: '8px',
                                  background: '#ffffff',
                                  overflow: 'hidden'
                                }}
                              >
                                <div
                                  style={{
                                    backgroundColor: '#f5f5f5',
                                    padding: '0.9rem 1rem',
                                    fontSize: 'clamp(10px, 2.5vw, 12px)'
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: '2fr 1fr 2fr',
                                      columnGap: '1.5rem',
                                      rowGap: '0.35rem'
                                    }}
                                  >
                                    <div>
                                      <div style={{ fontWeight: 700, fontSize: '1.02em', marginBottom: '0.35rem' }}>{displayText(" Case ")}{caseName}
                                      </div>
                                      <div><span style={{ fontWeight: 700 }}>Firm </span><span>{caseFirm}</span></div>
                                      <div style={{ marginTop: '0.25rem' }}><span style={{ fontWeight: 700 }}>Contact </span><span>{caseContact}</span></div>
                                      <div style={{ marginTop: '0.25rem' }}><span style={{ fontWeight: 700 }}>Address </span><span>{caseAddress}</span></div>
                                    </div>
                                    <div style={{ alignSelf: 'center' }}>
                                      <div><span style={{ fontWeight: 700 }}>Phone </span><span>{casePhone}</span></div>
                                    </div>
                                    <div>
                                      <div><span style={{ fontWeight: 700 }}>{displayText("Case/Matter ")}</span><span>{caseMatter}</span></div>
                                      <div style={{ marginTop: '0.25rem' }}><span style={{ fontWeight: 700 }}>Created Date </span><span>{rowCreated}</span></div>
                                      <div style={{ marginTop: '0.25rem' }}><span style={{ fontWeight: 700 }}>Email </span><span>{caseEmail}</span></div>
                                    </div>
                                  </div>
                                </div>

                                <table
                                  style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: 'clamp(10px, 2.5vw, 12px)'
                                  }}
                                >
                                  <thead>
                                    <tr style={{ backgroundColor: '#9e9e9e', color: '#ffffff' }}>
                                      <th style={{ padding: '0.6rem', textAlign: 'left' }}>{clientUserText("User")}</th>
                                      <th style={{ padding: '0.6rem', textAlign: 'left' }}>Username</th>
                                      <th style={{ padding: '0.6rem', textAlign: 'left' }}>City</th>
                                      <th style={{ padding: '0.6rem', textAlign: 'left' }}>State/Region</th>
                                      <th style={{ padding: '0.6rem', textAlign: 'left' }}>Created</th>
                                      <th style={{ padding: '0.6rem', textAlign: 'left' }}>Deleted</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td style={{ padding: '0.6rem' }}>{userCell}</td>
                                      <td style={{ padding: '0.6rem' }}>{usernameCell}</td>
                                      <td style={{ padding: '0.6rem' }}>{rowCity}</td>
                                      <td style={{ padding: '0.6rem' }}>{rowState}</td>
                                      <td style={{ padding: '0.6rem' }}>{rowCreated}</td>
                                      <td style={{ padding: '0.6rem' }}>{rowDeleted}</td>
                                    </tr>
                                  </tbody>
                                </table>

                                <div
                                  style={{
                                    padding: '0.4rem 1rem',
                                    textAlign: 'right',
                                    fontSize: '0.85rem',
                                    backgroundColor: '#f5f5f5',
                                    borderTop: '1px solid #d1d1d1'
                                  }}
                                >{clientUserText(" Total Users 1 ")}</div>
                              </div>
                            );
                          })
                        ) : (
                          <div
                            style={{
                              marginTop: '1rem',
                              padding: '1rem',
                              border: `1px dashed ${theme.colors.borderColor}`,
                              borderRadius: '8px',
                              background: '#ffffff',
                              color: '#666',
                              textAlign: 'center'
                            }}
                          >
                            No information for this date range.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default UserReport;